/**
 * The material terrain is drawn with.
 *
 * A custom shader rather than MeshBasicMaterial for one reason: greedy-merged
 * quads carry a texture-array layer per vertex and UVs that run past 1.0, and
 * no stock Three.js material samples a sampler2DArray.
 *
 * Written in GLSL ES 3.00 (glslVersion: GLSL3), which is where sampler2DArray
 * lives. Three.js still injects position/normal/uv and the standard matrices;
 * everything else is declared here, including fog - hand-rolling four lines of
 * linear fog is simpler than reconciling the built-in fog chunks with GLSL3.
 */

import * as THREE from "three";

const VERTEX = /* glsl */ `
  in float layer;
  in float shade;
  in float light;

  out vec2 vUv;
  out float vLayer;
  out float vShade;
  out float vSky;
  out float vTorch;
  out float vFogDepth;

  void main() {
    vUv = uv;
    vLayer = layer;
    vShade = shade;
    // Packed as sky * 16 + block, both 0-15. Pulled apart here rather than in
    // the fragment shader: it is the same answer for the whole quad and this
    // runs four times instead of a few thousand.
    vSky = floor(light / 16.0) / 15.0;
    vTorch = mod(light, 16.0) / 15.0;

    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
    vFogDepth = -mvPosition.z;
    gl_Position = projectionMatrix * mvPosition;
  }
`;

const FRAGMENT = /* glsl */ `
  precision highp float;
  precision highp sampler2DArray;

  uniform sampler2DArray uAtlas;
  uniform vec3 uTint;
  uniform float uTime;
  uniform float uFlowLayer;
  uniform float uTorchLayer;
  /** The шопопало-блок, which has lightning running over it. */
  uniform float uMagicLayer;
  /** How much the sun is giving right now, 0 at midnight and 1 at noon. */
  uniform float uDaylight;
  uniform vec3 uFogColor;
  uniform float uFogNear;
  uniform float uFogFar;

  in vec2 vUv;
  in float vLayer;
  in float vShade;
  in float vSky;
  in float vTorch;
  in float vFogDepth;

  out vec4 outColor;

  void main() {
    // vUv runs 0..quadWidth; wrapping is RepeatWrapping on the texture, and
    // each block texture is its own layer, so nothing bleeds across tiles.
    vec2 uv = vUv;

    // Running water scrolls; the still sea does not. They are told apart by
    // layer rather than by a vertex attribute, which costs no memory and no
    // mesher changes - flowing water was given its own tile precisely so this
    // comparison could exist.
    //
    // Wrapping is what makes it seamless: the texture repeats, so an
    // ever-growing offset never shows a join, and the streaks on that tile are
    // drawn to wrap top to bottom for the same reason.
    if (abs(vLayer - uFlowLayer) < 0.5) {
      // Downward. On an upright face v runs with world height and the texture
      // uploads flipped, so subtracting here ran the water up the wall - which
      // is a thing water does not do, and it was the first thing anybody
      // noticed about it.
      uv.y += uTime * 0.55;
      // A slight sideways drift as well, so it reads as water rather than as
      // a conveyor belt.
      uv.x += sin(uTime * 0.7 + uv.y * 2.0) * 0.04;
    }

    vec4 texel = texture(uAtlas, vec3(uv, vLayer));

    // Leaves are cut out rather than blended: an alpha test needs no sorting
    // and no second render pass. The threshold is below half because mipmaps
    // average the holes away with distance, and a canopy that dissolves as you
    // walk from it looks worse than one that stays slightly too solid.
    if (texel.a < 0.4) discard;

    // Three things decide how bright this pixel is.
    //
    // vShade is the baked per-direction face shading - which way the surface
    // points, fixed at mesh time. vSky is how much of the open sky reaches
    // this face, and it is scaled by the hour: a cave mouth is bright at noon
    // and dim at dusk. vTorch is smoke and flame, and the hour does not touch
    // it, which is the whole reason a смолоскип is worth carrying underground.
    //
    // The floor is not zero. A cave with no light at all is a black
    // rectangle, and a player who cannot see the wall they are standing at
    // cannot tell the game from a crash.
    //
    // It has to be a floor on the *finished* light, not on the light before
    // shading. The first version put it before, so a downward-facing face in
    // a dark cave came out at 0.05 of 0.55 of a dark stone texture - about
    // three parts in a thousand, which is black by any measure anybody has.
    float sky = vSky * uDaylight;
    vec3 daylight = uTint * sky;
    // Torchlight is its own colour - fire is not daylight - and it does not
    // dim with the sky.
    vec3 flame = vec3(1.0, 0.78, 0.52) * vTorch;
    vec3 lit = max(daylight, flame) * vShade;
    // Twelve per cent, not five. Five of a stone texture is four parts in a
    // hundred of white, and the eye reads that as black; this is the least
    // that shows a player there is a wall in front of them.
    vec3 level = max(lit, vec3(0.12));

    // The смолоскип itself burns at full brightness whatever is around it,
    // told apart by its layer the same way flowing water is two dozen lines
    // up. Without this a torch in a dark cave draws itself in its own gloom.
    float selfLit = abs(vLayer - uTorchLayer) < 0.5 ? 1.0 : 0.0;
    vec3 color = texel.rgb * mix(min(level, vec3(1.0)), vec3(1.0), selfLit);

    /*
     * Шопопало-блок: white lightning, crossing it and dying away.
     *
     * "Зроби його темно коричневим з білими блискавками які проходять по
     * ньому та зникають." Drawn here rather than painted into the tile,
     * because a spark that does not move is a scratch.
     *
     * Three bolts at a time, each with its own clock. A bolt is a bright
     * line across the face, bent by a sine so it forks rather than rules,
     * and its life is one second: it strikes white, thins, and is gone. The
     * face's own uv is what it is drawn in, so every side of the block has
     * its own weather.
     */
    if (abs(vLayer - uMagicLayer) < 0.5) {
      /*
       * Three bolts, each crossing the face and dying.
       *
       * The first try drew a wave of "fract" along one axis, which is a set
       * of stripes across the whole block - a venetian blind, not lightning.
       * A bolt is a *line*: one crooked path down the face, thin, with a
       * head that runs ahead of it and a tail that fades out behind.
       *
       * "fract(vUv)" because the mesher merges faces and the uv on a merged
       * quad runs past one - a lone block never is merged, but a wall of
       * them would be, and a bolt is per face.
       */
      vec2 uvm = fract(vUv);
      float glow = 0.0;
      for (int i = 0; i < 3; i++) {
        float n = float(i);
        float cycle = uTime * 0.85 + n * 0.41;
        float strike = floor(cycle);
        float t = fract(cycle);
        // Where this strike happens, and how it leans - new every time.
        float pick = fract(sin(strike * 91.7 + n * 13.3) * 43758.5453);
        float lean = (pick - 0.5) * 0.6;
        float path = 0.15 + pick * 0.7 + lean * (uvm.y - 0.5)
          + sin(uvm.y * 13.0 + strike * 2.1) * 0.045
          + sin(uvm.y * 31.0 + strike) * 0.018;
        float d = abs(uvm.x - path);
        // The head runs down the face; what is behind it is already lit.
        float head = 1.25 - t * 1.6;
        float along = smoothstep(head - 0.45, head, uvm.y) *
          smoothstep(head + 0.06, head - 0.02, uvm.y - 0.0);
        float core = smoothstep(0.016, 0.0, d);
        float halo = smoothstep(0.06, 0.0, d) * 0.28;
        glow += (core + halo) * along * (1.0 - t * t);
      }
      color += vec3(1.0, 0.98, 0.92) * clamp(glow, 0.0, 1.4);
    }

    float fog = smoothstep(uFogNear, uFogFar, vFogDepth);

    // The texture's own alpha, not a hard 1.0.
    //
    // Writing 1.0 here meant the blended pass blended nothing: water came out
    // as solid paint however transparent its texture was, and the alpha in the
    // tile - 186 of 255 since the day water existed - had no effect on
    // anything at all. Opaque blocks are unaffected: their alpha is already 1,
    // and their material does not blend regardless.
    //
    // Fog is opaque, though. Something lost in the distance has to become the
    // fog rather than fading to nothing, or a far-off lake turns into a hole.
    outColor = vec4(mix(color, uFogColor, fog), mix(texel.a, 1.0, fog));
  }
`;

/**
 * Three.js wants an index signature on a uniforms object, so this is declared
 * as a type with one rather than a plain interface.
 */
export type VoxelMaterialUniforms = {
  uAtlas: { value: THREE.DataArrayTexture };
  uTint: { value: THREE.Color };
  uTime: { value: number };
  uFlowLayer: { value: number };
  uTorchLayer: { value: number };
  uMagicLayer: { value: number };
  uDaylight: { value: number };
  uFogColor: { value: THREE.Color };
  uFogNear: { value: number };
  uFogFar: { value: number };
} & { [uniform: string]: THREE.IUniform };

/**
 * Build the two terrain materials.
 *
 * They share every uniform object by reference, so updating the day/night tint
 * or the fog once updates both - there is no way for water to end up lit
 * differently from the ground it sits on.
 *
 * The blended one does not write depth: water must not hide the lake bed
 * behind it. Three.js renders all opaque geometry before any transparent
 * geometry, so terrain behind the water still draws correctly.
 */
export function createVoxelMaterials(
  atlas: THREE.DataArrayTexture,
  fogNear: number,
  fogFar: number,
  flowLayer: number,
  torchLayer: number,
  magicLayer: number,
): { opaque: THREE.ShaderMaterial; blended: THREE.ShaderMaterial;
     uniforms: VoxelMaterialUniforms } {
  const uniforms: VoxelMaterialUniforms = {
    uAtlas: { value: atlas },
    uTint: { value: new THREE.Color(0xffffff) },
    uTime: { value: 0 },
    uFlowLayer: { value: flowLayer },
    uTorchLayer: { value: torchLayer },
    uMagicLayer: { value: magicLayer },
    uDaylight: { value: 1 },
    uFogColor: { value: new THREE.Color(0xffffff) },
    uFogNear: { value: fogNear },
    uFogFar: { value: fogFar },
  };

  const opaque = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    uniforms,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
  });

  const blended = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    uniforms,
    vertexShader: VERTEX,
    fragmentShader: FRAGMENT,
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide, // a water surface is worth seeing from underneath
  });

  return { opaque, blended, uniforms };
}
